import React from "react"
import { Buffer } from "buffer"

import { useSharedValue } from "react-native-reanimated"

import { Alert } from "src/components/Alert"
import { useMicrophone } from "src/context/MicrophoneContext"
import { useWebsocket } from "src/context/WebsocketContext"
import { useRestartableWait } from "src/hooks/useRestartableWait"
import { delay } from "src/utils/delay"

// Audio Constants:
// These are defined here rather than imported from useMicrophoneStream (the native file) because
// on web the platform resolver maps that import back to this file, causing a circular dependency
// where all the imported constants would be undefined at runtime.
export const SAMPLE_RATE_BASE = 16000 // [Hz]
export const BUFFERED_AUDIO_MAX_TIMEOUT = 2500 // [ms]
export const BUFFERED_AUDIO_RESTART_TIMEOUT = 500 // [ms]
export const EARLY_MICROPHONE_STOP_DELAY = 500 // [ms]
const _dbfsOffset = 60

// Module-level stream generation counter.
//
// Every call to startStream claims a new generation number. The onaudioprocess
// closure captures that number and compares it to the module-level counter before
// forwarding audio chunks. If a SpeechInputButton remounts mid-session, the new
// startStream increments the counter, which silently mutes any still-connected
// ScriptProcessorNode from a previous hook instance — preventing the "every word
// doubled" duplicate-transcription bug caused by two concurrent audio pipelines.
let _currentStreamGeneration = 0

// Module-level starting guard.
//
// Unlike isStartingStream.current (a per-instance ref), this flag is shared
// across ALL hook instances. React state updates are async, so two component
// instances that mount in the same render pass can both see isLoading=false and
// race into startStream simultaneously. A module-level boolean is set
// synchronously at the top of startStream and cleared in its finally block,
// which reliably prevents a remounted SpeechInputButton from launching a second
// getUserMedia call while the first instance's hardware initialization is still
// in progress.
let _isStreamStarting = false

// Module-level hardware refs.
//
// When SpeechInputButton remounts, the NEW hook instance's startStream is blocked
// by _isStreamStarting — so the new instance's per-instance refs stay null and it
// can never disconnect the OLD instance's hardware. Making these module-level means
// ANY hook instance (including the new, unstarted one) can reach the active hardware
// and tear it down when the user presses stop.
let _processor: ScriptProcessorNode | null = null
let _analyser: AnalyserNode | null = null
let _stream: MediaStream | null = null
let _audioContext: AudioContext | null = null

/**
 * Web-specific microphone stream hook that uses Web Audio API.
 *
 * @param onError - Optional callback fired when startStream fails to enter listening
 *   state for a reason other than user-initiated cancellation (websocket disconnected,
 *   getUserMedia error). Used by the parent to clear mic-loading state without waiting
 *   for a backend `streaming_ready` event that will never arrive.
 */
export const useMicrophoneStream = (
  onStart: () => void,
  onStop: () => void,
  onChunk: (chunk: any) => void,
  onError?: () => void,
) => {
  // Use global loading and listening state from MicrophoneContext
  const { isListening, setIsListening, isLoading, setIsLoading } = useMicrophone()
  const { isConnected } = useWebsocket()
  const [_hasPermission, setHasPermission] = React.useState<boolean | null>(null)
  const volume = useSharedValue<number>(0)

  // FIXME: When the Web Audio API processor is stopped we need to wait some
  // amount of time for more audio chunks to be delivered to the listeners.
  // This is because we can't directly observe how much audio is still
  // buffered by the Web Audio API.
  // A small timeout is restarted whenever a new chunk is received (up to the maximum timeout).
  const {
    startWait: waitForBufferedAudio,
    restartWait: waitForBufferedAudioNextChunk,
    cancelWait: cancelWaitForBufferedAudio,
    isWaiting: isWaitingForBufferedAudio,
  } = useRestartableWait(BUFFERED_AUDIO_RESTART_TIMEOUT, BUFFERED_AUDIO_MAX_TIMEOUT)

  // Tracks the stream generation that this hook instance most recently started.
  // Initialized to -1 (unstarted). Set to the active generation in startStream.
  //
  // Used to distinguish two different call sites for stopStream/resetDefaults:
  //   • "Stale old instance" — myStreamGenerationRef.current was a real generation
  //     (> -1) but is now less than _currentStreamGeneration. This is an unmount
  //     cleanup from a superseded instance; it must NOT clobber the newer stream's
  //     isListening state.
  //   • "Fresh unstarted instance" — myStreamGenerationRef.current is still -1
  //     because startStream was blocked by _isStreamStarting. The user has pressed
  //     stop on the NEW button; this instance IS the active UI and SHOULD update
  //     state and tear down the shared hardware.
  //   • "Active instance" — myStreamGenerationRef.current === _currentStreamGeneration.
  //     Normal case; always allowed to update state.
  const myStreamGenerationRef = React.useRef(-1)

  const resetDefaults = React.useCallback(() => {
    volume.value = 0
    cancelWaitForBufferedAudio()
    // Block state updates only for stale old instances (had a real generation that
    // has since been superseded). Unstarted instances (gen -1) and the active
    // instance are both allowed to update shared context state.
    const isStaleCleanup =
      myStreamGenerationRef.current > -1 && myStreamGenerationRef.current < _currentStreamGeneration
    if (!isStaleCleanup) {
      if (isListening) {
        onStop()
      }
      setIsLoading(false)
      setIsListening(false)
    }
  }, [onStop, volume, isListening, cancelWaitForBufferedAudio, setIsLoading, setIsListening])

  // Guard to prevent concurrent stopStream calls
  const isStoppingStream = React.useRef(false)
  // Cancellation token: set to true by stopStream so any in-progress startStream aborts
  const isCancelled = React.useRef(false)
  // Allows an incoming immediate stop to cut the non-immediate flush delay short.
  // The non-immediate stop sets this to a resolve function; an immediate stop calls it.
  const interruptDelayRef = React.useRef<(() => void) | null>(null)

  const stopStream = React.useCallback(
    async (options?: { immediate?: boolean }) => {
      const immediate = options?.immediate === true

      // Signal cancellation to any in-progress startStream (e.g. during getUserMedia)
      isCancelled.current = true

      // Prevent re-entrant calls: if already stopping, bail out.
      // Exception: if an immediate stop arrives while a non-immediate one is waiting
      // through the flush delay, interrupt the delay so hardware tears down right away.
      if (isStoppingStream.current) {
        if (immediate && interruptDelayRef.current) {
          console.log("[MIC STREAM WEB] Immediate stop interrupting flush delay")
          interruptDelayRef.current()
        } else {
          console.log("[MIC STREAM WEB] Already stopping stream, ignoring duplicate call")
        }
        return
      }
      isStoppingStream.current = true

      try {
        console.log("[MIC STREAM WEB] stopping stream", { immediate })

        // Block state updates only for stale old instances (had a real generation that
        // has since been superseded). Unstarted instances (gen -1, e.g. a remounted
        // SpeechInputButton whose startStream was blocked) and the active instance are
        // both allowed to update shared listening state and tear down hardware.
        const isStaleCleanup =
          myStreamGenerationRef.current > -1 &&
          myStreamGenerationRef.current < _currentStreamGeneration
        if (isListening && !isStaleCleanup) {
          setIsListening(false)
          if (!immediate) {
            // Normal stop: wait for trailing audio so the last words aren't cut off.
            // The delay is interruptible — an incoming immediate stop resolves it early.
            console.log(
              `[MIC STREAM WEB] Waiting ${EARLY_MICROPHONE_STOP_DELAY}ms for audio buffer to flush...`,
            )
            await Promise.race([
              delay(EARLY_MICROPHONE_STOP_DELAY),
              new Promise<void>((resolve) => {
                interruptDelayRef.current = resolve
              }),
            ])
            interruptDelayRef.current = null
          } else {
            console.log("[MIC STREAM WEB] Stopping microphone immediately (e.g. cancel/background)")
          }
        }

        // Disconnect the ScriptProcessorNode first so onaudioprocess stops firing and
        // no more chunks are sent to the server.
        // These are module-level vars so any hook instance (including a remounted one
        // whose startStream was blocked) can reach and tear down the active hardware.
        if (_processor) {
          _processor.disconnect()
          _processor = null
        }

        if (_analyser) {
          _analyser.disconnect()
          _analyser = null
        }

        // Stop media stream tracks
        if (_stream) {
          _stream.getTracks().forEach((track) => track.stop())
          _stream = null
        }

        // Close audio context
        if (_audioContext && _audioContext.state !== "closed") {
          await _audioContext.close()
          _audioContext = null
        }

        // In normal (non-immediate) mode, wait for any remaining buffered chunks that the
        // Web Audio API may have already dispatched before the disconnect.
        // In immediate mode we skip this so we don't linger and risk sending stale audio.
        if (!immediate) {
          await waitForBufferedAudio()
        }
      } catch (e) {
        console.warn("Error stopping web audio stream", e)
        // Continue to reset state even if stop fails
      } finally {
        isStoppingStream.current = false
        // Do NOT reset isCancelled here. startStream resets it at the top so that if
        // stopStream completes before an in-flight getUserMedia returns, the cancellation
        // signal is still visible to the check that follows getUserMedia.
      }

      // Reset all state and notify server
      resetDefaults()
    },
    [resetDefaults, isListening, setIsListening, waitForBufferedAudio],
  )

  const calculateVolume = React.useCallback((dataArray: Uint8Array): number => {
    const sum = dataArray.reduce((acc, value) => acc + (value - 128) ** 2, 0)
    const rms = Math.sqrt(sum / dataArray.length) / 128.0
    const dbfs = 20 * Math.log10(Math.max(rms, 1e-9))

    // normalized scale of 0-1
    const normalized = Math.min(1, Math.max(0, dbfs + _dbfsOffset) / _dbfsOffset)

    // normalized scale of 0-10
    return normalized * 10
  }, [])

  const startStream = React.useCallback(async () => {
    // Module-level guard: shared across all hook instances (unlike a per-instance ref).
    // React state updates are async, so two component instances that mount in the same
    // render pass can both see isLoading=false and race into getUserMedia simultaneously.
    // This boolean is set synchronously and blocks the second caller before any async work.
    if (_isStreamStarting) {
      console.log(
        "[MIC STREAM WEB] Another instance is already starting stream (module-level guard), skipping",
      )
      return
    }
    _isStreamStarting = true

    // Fresh start — clear any lingering cancellation signal
    isCancelled.current = false

    // Claim the next generation. Any ScriptProcessorNode from a previous startStream
    // call (on this instance or a prior hook instance) will see its captured generation
    // no longer matches _currentStreamGeneration and will drop its audio output.
    _currentStreamGeneration++
    const streamGeneration = _currentStreamGeneration
    // Record which generation this hook instance owns so resetDefaults and stopStream
    // can skip global state updates if a newer instance has already taken over.
    myStreamGenerationRef.current = streamGeneration
    console.log("[MIC STREAM WEB] Starting stream, generation:", streamGeneration)

    try {
      // Tear down any orphaned stream from a previous session (e.g. after HMR / hot-refresh)
      // before allocating new hardware resources.
      if (_processor) {
        _processor.disconnect()
        _processor = null
      }
      if (_analyser) {
        _analyser.disconnect()
        _analyser = null
      }
      if (_stream) {
        _stream.getTracks().forEach((track) => track.stop())
        _stream = null
      }
      if (_audioContext && _audioContext.state !== "closed") {
        await _audioContext.close()
        _audioContext = null
      }

      // Do not forcibly stop speech here; wait for streaming_ready to true

      setIsLoading(true)

      // Bail out early if the websocket is not connected. Without an open socket, the
      // backend will never see "start streaming" and will never send `streaming_ready`,
      // so the parent's mic-loading state would otherwise spin indefinitely.
      if (!isConnected) {
        console.warn("[MIC STREAM WEB] Cannot start stream: websocket is not connected")
        resetDefaults()
        onError?.()
        return
      }

      // Request microphone permission
      console.log("[MIC STREAM WEB] Requesting microphone permission...")
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SAMPLE_RATE_BASE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      })
      console.log("[MIC STREAM WEB] Microphone permission granted, stream created")

      // Check if stopStream was called while we were awaiting getUserMedia.
      // If so, release the track immediately and abort — don't open a new audio session.
      if (isCancelled.current) {
        console.log("[MIC STREAM WEB] Cancelled during getUserMedia, releasing tracks")
        stream.getTracks().forEach((track) => track.stop())
        resetDefaults()
        return
      }

      // Check if a newer startStream (on any hook instance) superseded us while getUserMedia
      // was pending. This happens when SpeechInputButton remounts mid-session — the new
      // instance's startStream increments the generation before this one's getUserMedia
      // resolves. Abort silently without calling onStart(); the newer instance owns the
      // global state and will send its own "start_streaming" message.
      if (_currentStreamGeneration !== streamGeneration) {
        console.log(
          "[MIC STREAM WEB] Superseded by generation",
          _currentStreamGeneration,
          "— aborting gen",
          streamGeneration,
        )
        stream.getTracks().forEach((track) => track.stop())
        return
      }

      _stream = stream
      setHasPermission(true)

      // Create audio context
      console.log("[MIC STREAM WEB] Creating audio context...")
      _audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: SAMPLE_RATE_BASE,
      })

      const audioContext = _audioContext
      console.log("[MIC STREAM WEB] Audio context state:", audioContext.state)

      const source = audioContext.createMediaStreamSource(stream)
      console.log("[MIC STREAM WEB] Media stream source created")

      // Create analyser for volume calculation
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 2048
      _analyser = analyser
      console.log("[MIC STREAM WEB] Analyser created")

      // Create script processor for audio data
      const processor = audioContext.createScriptProcessor(4096, 1, 1)
      _processor = processor
      console.log("[MIC STREAM WEB] Script processor created")

      const dataArray = new Uint8Array(analyser.frequencyBinCount)

      processor.onaudioprocess = (e) => {
        // Guard: if a newer startStream has been called (possibly on a different hook
        // instance that remounted), this processor has been superseded. Drop the audio
        // to prevent duplicate chunks being sent to the server.
        if (_currentStreamGeneration !== streamGeneration) {
          console.log(
            "[MIC STREAM WEB] Dropping audio from stale processor (gen",
            streamGeneration,
            "!= current",
            _currentStreamGeneration,
            ")",
          )
          return
        }

        // Guard: stopStream nulls _processor before calling .disconnect().
        // The Web Audio API may have already queued onaudioprocess callbacks that fire
        // after disconnect — bail out so no chunks are forwarded post-teardown.
        if (!_processor) {
          return
        }

        // Check if recording has stopped and we're waiting for buffered audio.
        // If we are, restart the wait whenever a new chunk is received.
        if (isWaitingForBufferedAudio()) {
          waitForBufferedAudioNextChunk()
        }

        const inputBuffer = e.inputBuffer
        const inputData = inputBuffer.getChannelData(0)

        // Convert Float32Array to Int16Array (matching native implementation)
        const int16Array = new Int16Array(inputData.length)
        for (let i = 0; i < inputData.length; i++) {
          int16Array[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32767))
        }

        // Create buffer chunk
        const chunk = Buffer.from(int16Array.buffer)
        console.log("[MIC STREAM WEB] Audio chunk created, size:", chunk.length, "bytes")
        console.log("[MIC STREAM WEB] Int16Array length:", int16Array.length)
        console.log("[MIC STREAM WEB] Sample rate:", SAMPLE_RATE_BASE)
        onChunk(chunk)

        // Calculate volume
        analyser.getByteFrequencyData(dataArray)
        const calculatedVolume = calculateVolume(dataArray)
        volume.value = calculatedVolume
      }

      // Connect the audio graph
      console.log("[MIC STREAM WEB] Connecting audio graph...")
      source.connect(analyser)
      analyser.connect(processor)
      processor.connect(audioContext.destination)
      console.log("[MIC STREAM WEB] Audio graph connected")

      // Re-check connection: getUserMedia and audio-context setup can take long enough
      // for the websocket to drop. Sending "start streaming" with a dead socket would
      // leave the parent stuck waiting for `streaming_ready`.
      if (!isConnected) {
        console.warn(
          "[MIC STREAM WEB] Websocket disconnected during initialization; aborting stream start",
        )
        if (_processor) {
          _processor.disconnect()
          _processor = null
        }
        if (_analyser) {
          _analyser.disconnect()
          _analyser = null
        }
        stream.getTracks().forEach((track) => track.stop())
        _stream = null
        if (_audioContext && _audioContext.state !== "closed") {
          await _audioContext.close()
          _audioContext = null
        }
        resetDefaults()
        onError?.()
        return
      }

      onStart()
      setIsListening(true)
      setIsLoading(false)
      console.log("[MIC STREAM WEB] Audio stream started successfully")
    } catch (error) {
      console.error("[MIC STREAM WEB] Error starting web audio stream:", error)
      setHasPermission(false)

      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          Alert.alert(
            "Microphone Permission Denied",
            "Please allow microphone access in your browser settings.",
          )
        } else if (error.name === "NotFoundError") {
          Alert.alert("No Microphone Found", "Please connect a microphone and try again.")
        } else {
          Alert.alert(
            "Microphone Error",
            "Unable to access microphone. Please check your browser settings.",
          )
        }
      }

      resetDefaults()
      onError?.()
    } finally {
      _isStreamStarting = false
    }
  }, [
    onStart,
    onError,
    setIsLoading,
    setIsListening,
    isWaitingForBufferedAudio,
    onChunk,
    calculateVolume,
    volume,
    waitForBufferedAudioNextChunk,
    resetDefaults,
    isConnected,
  ])

  return {
    startStream,
    stopStream,
    volume,
    isListening,
    isLoading,
  }
}
